/**
 * Конструктор шаблонов карточек знаков Визуализатора (lifecycle + секции/поля + JSON I/O).
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Map, Plus, RefreshCw, Upload } from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuthV2";
import { fetchCardTemplateCatalog } from "@/services/card-template-catalog.service";
import {
  visualizerSignCardApi,
  type VisualizerSignCardFieldPayload,
} from "@/services/visualizer-sign-card-api.service";
import type { VisualizerSignCardTemplateWithDetails } from "@/types/visualizer-sign-card-template";

const STATUS_LABEL: Record<string, string> = {
  draft: "Черновик",
  published: "Опубликован",
  archived: "Архив",
};

type ImportConflict = "error" | "skip" | "suffix";

export default function VisualizerSignCardTemplateManagement() {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("visualizer_sign_card_templates.manage");
  const canPublish = hasPermission("templates.publish");
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement>(null);

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [sectionCode, setSectionCode] = useState("main");
  const [sectionName, setSectionName] = useState("Основное");
  const [fieldKey, setFieldKey] = useState("");
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldQualId, setFieldQualId] = useState("1");
  const [onConflict, setOnConflict] = useState<ImportConflict>("suffix");

  const catalogQuery = useQuery({
    queryKey: ["card-template-catalog", "visualizer", statusFilter],
    queryFn: () =>
      fetchCardTemplateCatalog({
        module: "visualizer",
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 100,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ["visualizer-sign-card-template", selectedId],
    queryFn: () => visualizerSignCardApi.get(selectedId as number),
    enabled: selectedId != null,
  });

  const widgetsQuery = useQuery({
    queryKey: ["visualizer-sign-card-widgets"],
    queryFn: () => visualizerSignCardApi.listFieldWidgetTypes(),
    enabled: canManage,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["card-template-catalog", "visualizer"] });
    if (selectedId != null) {
      await queryClient.invalidateQueries({ queryKey: ["visualizer-sign-card-template", selectedId] });
    }
  };

  const createMut = useMutation({
    mutationFn: () =>
      visualizerSignCardApi.create({
        code: newCode.trim(),
        name: newName.trim(),
        is_default: false,
      }),
    onSuccess: async (tpl) => {
      setNewCode("");
      setNewName("");
      setSelectedId(tpl.id);
      await invalidate();
      toast({ title: "Черновик создан" });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const publishMut = useMutation({
    mutationFn: (id: number) => visualizerSignCardApi.publish(id),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Опубликовано" });
    },
    onError: (e: Error) => toast({ title: "Публикация", description: e.message, variant: "destructive" }),
  });

  const archiveMut = useMutation({
    mutationFn: (id: number) => visualizerSignCardApi.archive(id),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "В архиве" });
    },
    onError: (e: Error) => toast({ title: "Архив", description: e.message, variant: "destructive" }),
  });

  const restoreMut = useMutation({
    mutationFn: (id: number) => visualizerSignCardApi.restore(id, { restore_as_non_default: true }),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Восстановлено" });
    },
    onError: (e: Error) => toast({ title: "Restore", description: e.message, variant: "destructive" }),
  });

  const addSectionMut = useMutation({
    mutationFn: () =>
      visualizerSignCardApi.addSection(selectedId as number, {
        code: sectionCode.trim(),
        name: sectionName.trim(),
      }),
    onSuccess: async () => {
      await invalidate();
      toast({ title: "Секция добавлена" });
    },
    onError: (e: Error) => toast({ title: "Секция", description: e.message, variant: "destructive" }),
  });

  const defaultWidget = widgetsQuery.data?.[0];

  const addFieldMut = useMutation({
    mutationFn: () => {
      const widgetCode = (defaultWidget?.kls_code || "string").toLowerCase();
      const payload: VisualizerSignCardFieldPayload = {
        field_key: fieldKey.trim(),
        label: fieldLabel.trim(),
        field_type: (widgetCode as VisualizerSignCardFieldPayload["field_type"]) || "string",
        field_type_kls_id: defaultWidget?.kls_id ?? null,
        qual_id: Number(fieldQualId) || 1,
        section_id: detailQuery.data?.sections?.[0]?.id ?? null,
      };
      return visualizerSignCardApi.addField(selectedId as number, payload);
    },
    onSuccess: async () => {
      setFieldKey("");
      setFieldLabel("");
      await invalidate();
      toast({ title: "Поле добавлено" });
    },
    onError: (e: Error) => toast({ title: "Поле", description: e.message, variant: "destructive" }),
  });

  const importMut = useMutation({
    mutationFn: ({ text, conflict }: { text: string; conflict: ImportConflict }) => {
      const bundle = JSON.parse(text) as Record<string, unknown>;
      return visualizerSignCardApi.importTemplatesJson(bundle, conflict);
    },
    onSuccess: async (res) => {
      const created = res.results.filter((r) => r.outcome === "created").length;
      toast({
        title: "Импорт завершён",
        description: `Создано черновиков: ${created}, всего записей: ${res.results.length}`,
      });
      await invalidate();
    },
    onError: (e: Error) =>
      toast({ title: "Импорт JSON", description: e.message, variant: "destructive" }),
  });

  const downloadExport = useCallback(
    async (id: number, code: string) => {
      try {
        const data = await visualizerSignCardApi.exportTemplateJson(id);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `visualizer-sign-card-template-${code}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast({
          title: "Экспорт",
          description: e instanceof Error ? e.message : "Ошибка",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  const downloadBulkExport = useCallback(async () => {
    const ids = (catalogQuery.data?.items ?? []).map((r) => r.id);
    if (ids.length === 0) {
      toast({ title: "Экспорт", description: "Список пуст", variant: "destructive" });
      return;
    }
    try {
      const data = await visualizerSignCardApi.exportTemplatesBulk(ids.slice(0, 50));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "visualizer-sign-card-templates-export.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Экспорт списка",
        description: e instanceof Error ? e.message : "Ошибка",
        variant: "destructive",
      });
    }
  }, [catalogQuery.data?.items, toast]);

  const ready = catalogQuery.data?.module_ready !== false;
  const items = catalogQuery.data?.items ?? [];
  const detail: VisualizerSignCardTemplateWithDetails | undefined = detailQuery.data;
  const isDraft = detail?.status === "draft";

  const statusBadge = useMemo(() => {
    if (!detail) return null;
    return <Badge variant="secondary">{STATUS_LABEL[detail.status] ?? detail.status}</Badge>;
  }, [detail]);

  if (!ready) {
    return (
      <p className="text-sm text-muted-foreground">
        {catalogQuery.data?.message || "Модуль шаблонов знаков Визуализатора недоступен."}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Map className="h-4 w-4" />
        <h3 className="font-medium">Шаблоны знаков Визуализатора</h3>
        <SectionHelpTooltip label="Справка: шаблоны знака">
          <p>Конструктор структуры карточки условного знака: draft → publish → archive.</p>
          <p>Scope — symbol_kls_id (KLS знака) или универсальный (пусто). Runtime-значения живут на знаке сцены.</p>
          <p>Meta-rules и geometry_rules в этой версии нет.</p>
        </SectionHelpTooltip>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto"
          onClick={() => void catalogQuery.refetch()}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 border rounded-md p-3">
        <div className="flex items-center gap-1 mr-2">
          <span className="text-sm font-medium">Импорт / экспорт</span>
          <SectionHelpTooltip label="Справка: I/O шаблонов знака">
            <p>Перенос структуры шаблона между контурами в JSON-пакете geopulseml.visualizer_sign_card_template_export v1.</p>
            <ul className="list-disc pl-4 space-y-1 mt-1">
              <li>Экспорт — выбранный шаблон или весь текущий список (до 50).</li>
              <li>Импорт создаёт только черновики; конфликт кода: error / skip / suffix.</li>
              <li>is_default при импорте сбрасывается — default назначайте после publish.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!detail}
          onClick={() => detail && void downloadExport(detail.id, detail.code)}
        >
          <Download className="h-4 w-4 mr-1" />
          Экспорт выбранного
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => void downloadBulkExport()}>
          <Download className="h-4 w-4 mr-1" />
          Экспорт списка
        </Button>
        {canManage && (
          <>
            <div className="space-y-1">
              <Label className="text-xs">При конфликте code</Label>
              <Select value={onConflict} onValueChange={(v) => setOnConflict(v as ImportConflict)}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="error">error</SelectItem>
                  <SelectItem value="skip">skip</SelectItem>
                  <SelectItem value="suffix">suffix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={async (ev) => {
                const f = ev.target.files?.[0];
                ev.target.value = "";
                if (!f) return;
                try {
                  const text = await f.text();
                  importMut.mutate({ text, conflict: onConflict });
                } catch (e) {
                  toast({
                    title: "Файл",
                    description: e instanceof Error ? e.message : "Не удалось прочитать файл",
                    variant: "destructive",
                  });
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={importMut.isPending}
              onClick={() => importInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-1" />
              Импорт JSON
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label>Статус</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="all">Все</SelectItem>
              <SelectItem value="draft">Черновик</SelectItem>
              <SelectItem value="published">Опубликован</SelectItem>
              <SelectItem value="archived">Архив</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <>
            <div className="space-y-1">
              <Label>Код</Label>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label>Имя</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-48" />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={!newCode.trim() || !newName.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Создать
            </Button>
          </>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ul className="space-y-1 border rounded-md p-2 max-h-[420px] overflow-y-auto text-sm">
          {items.length === 0 ? (
            <li className="text-muted-foreground px-2 py-1">Список пуст</li>
          ) : (
            items.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`w-full text-left rounded px-2 py-1.5 hover:bg-muted ${
                    selectedId === row.id ? "bg-muted font-medium" : ""
                  }`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <span className="block truncate">{row.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {row.code} · {STATUS_LABEL[row.status] ?? row.status}
                    {row.symbol_kls_id != null ? ` · kls ${row.symbol_kls_id}` : " · универсальный"}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>

        <div className="border rounded-md p-3 space-y-3 text-sm min-h-[200px]">
          {!detail ? (
            <p className="text-muted-foreground">Выберите шаблон</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium">{detail.name}</div>
                {statusBadge}
                <span className="text-xs text-muted-foreground">v{detail.version}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                code={detail.code}; symbol_kls_id={detail.symbol_kls_id ?? "null"}
              </p>
              <div className="flex flex-wrap gap-2">
                {canPublish && detail.status === "draft" && (
                  <Button size="sm" onClick={() => publishMut.mutate(detail.id)}>
                    Publish
                  </Button>
                )}
                {canPublish && detail.status === "published" && (
                  <Button size="sm" variant="outline" onClick={() => archiveMut.mutate(detail.id)}>
                    Archive
                  </Button>
                )}
                {canPublish && detail.status === "archived" && (
                  <Button size="sm" variant="outline" onClick={() => restoreMut.mutate(detail.id)}>
                    Restore
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void downloadExport(detail.id, detail.code)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  JSON
                </Button>
              </div>

              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-1">Секции</h4>
                <ul className="space-y-0.5 mb-2">
                  {(detail.sections || []).map((s) => (
                    <li key={s.id}>
                      {s.name} <span className="text-muted-foreground">({s.code})</span>
                    </li>
                  ))}
                </ul>
                {canManage && isDraft && (
                  <div className="flex gap-1 flex-wrap">
                    <Input
                      className="w-28"
                      value={sectionCode}
                      onChange={(e) => setSectionCode(e.target.value)}
                      placeholder="code"
                    />
                    <Input
                      className="w-36"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                      placeholder="name"
                    />
                    <Button size="sm" variant="outline" onClick={() => addSectionMut.mutate()}>
                      + секция
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-xs uppercase text-muted-foreground mb-1">Поля</h4>
                <ul className="space-y-0.5 mb-2">
                  {(detail.fields || []).map((f) => (
                    <li key={f.id}>
                      {f.label} <span className="text-muted-foreground">({f.field_key})</span>
                    </li>
                  ))}
                </ul>
                {canManage && isDraft && (
                  <div className="flex gap-1 flex-wrap items-center">
                    <Input
                      className="w-28"
                      value={fieldKey}
                      onChange={(e) => setFieldKey(e.target.value)}
                      placeholder="field_key"
                    />
                    <Input
                      className="w-36"
                      value={fieldLabel}
                      onChange={(e) => setFieldLabel(e.target.value)}
                      placeholder="label"
                    />
                    <Input
                      className="w-20"
                      value={fieldQualId}
                      onChange={(e) => setFieldQualId(e.target.value)}
                      placeholder="qual"
                      title="qual_id"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!fieldKey.trim() || !fieldLabel.trim()}
                      onClick={() => addFieldMut.mutate()}
                    >
                      + поле
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
